export type NextStepButtonInput = {
  /** The screen stores itself (auto-save), so there is no Save button. */
  storesItself: boolean;
  /** A change is waiting to be stored, or is being stored. */
  hasPending: boolean;
  /** An ad was deleted and that deletion is not stored yet. */
  deletionsWaiting: boolean;
  /** The server refused the last store. */
  refused: boolean;
  /** Only read when the screen has a Save button. */
  hasUnsavedChanges: boolean;
};

/*
 * Whether "Go to next step" is drawn, and whether it can be pressed.
 *
 * On a screen that stores itself the button KEEPS ITS PLACE while a store is
 * waiting and only goes grey. It used to disappear for those seconds, and
 * Undo, Redo and "Saved at" slid over to where it had been and back again
 * (Steven's hand tests of 5 and 12 Oct 2026). Used by the ad builder and the
 * vacancy page; the targeting screen has the same rule inline.
 *
 * The top bar of every screen that stores itself reads, left to right:
 * this button (when the screen has one), Undo and Redo, then the save line
 * at the far right, 20 px apart. The button sits LEFT of the arrows so that
 * arrows and save line are at one place on every screen, with or without
 * it (Steven, 12 Oct 2026).
 *
 * With a Save button nothing changes: the Save button takes the place of
 * "Go to next step" while there is something to save.
 */
export const nextStepButton = ({
  storesItself,
  hasPending,
  deletionsWaiting,
  refused,
  hasUnsavedChanges,
}: NextStepButtonInput): { shown: boolean; disabled: boolean } => {
  if (!storesItself) return { shown: !hasUnsavedChanges, disabled: false };

  return { shown: true, disabled: hasPending || deletionsWaiting || refused };
};
